/**
 * TasksSection — Schedule section body.
 *
 * Section label reads "Tasks" on the Schedule sub-tab (2026-05-27
 * rename — the section title was "Schedule" in the magazine spread,
 * but now lives inside a sub-tab also called Schedule, so "Tasks"
 * removes the stutter). Underlying data model still uses "task"
 * (taskGuid, MiniProjectTask) — rename tracked separately.
 *
 * Uses MiniProjectTaskCard for visual consistency with Mission
 * (MiniProjectGoalCard) and Budget (MiniProjectBomItemCard). The pencil
 * on each card opens MiniProjectTaskEditTray. DEV-1123 (2026-05-26):
 * fetches team + roles once per render and shares with each card so the
 * assignee chip can resolve without N parallel fetches.
 *
 * Layout modes:
 *   - "flat" (default): single vertical list. Matches the legacy
 *     magazine-spread behavior.
 *   - "split": two side-by-side columns inside the Schedule sub-tab.
 *     Left column = recently completed (status === 'done'), sorted by
 *     completedAt descending (most-recently-completed first). Right
 *     column = next to be completed (status !== 'done'), sorted by
 *     due date ascending (no-due-date last), then position. Skip's
 *     2026-05-27 ask — the Lead is naturally interested in both
 *     "what did we just finish" and "what's coming up" at a glance.
 *
 * Strings localized via `pages:miniProject.sections.*` (shared form
 * vocabulary) and `pages:miniProject.sections.tasks.*` (task-specific).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useAppTranslation } from "../../../i18n/useAppTranslation";
import {
  fetchMiniProjectTeamMembers,
  type MiniProjectTask,
  type MiniProjectTeamMember,
} from "../../../api/miniProjects";
import {
  fetchMiniProjectRoles,
  type MiniProjectRole,
} from "../../../api/miniProjectRoles";
import MiniProjectTaskCard from "../../../Components/MiniProjectTaskCard/MiniProjectTaskCard";
import MiniProjectTaskEditTray from "../../../Components/MiniProjectTaskEdit/MiniProjectTaskEditTray";
import MiniProjectTaskDetailTray from "../../../Components/MiniProjectTaskDetail/MiniProjectTaskDetailTray";
import { getCurrentUserGuid } from "../../../api/client";
import styles from "./sections.module.css";

interface Props {
  miniProjectGuid: string;
  tasks: MiniProjectTask[];
  isLead: boolean;
  onMutated: () => void;
  /** Add-tray open state, lifted to ProgressTab so the SectionPanel
   *  header "+" button can trigger it (DEV-1081 Chunk D). */
  addOpen?: boolean;
  onCloseAdd?: () => void;
  /** Layout mode. Default "flat" preserves the magazine-spread behavior
   *  for any remaining consumers; "split" renders two columns
   *  (completed / next-up) on the Schedule sub-tab. */
  layout?: "flat" | "split";
}

/* ── Sort helpers ─────────────────────────────────────────────── */

/** Completed-tasks sort: most-recently-completed first. Falls back to
 *  updatedAt then position so rows never collide on a missing field. */
function sortRecentlyCompleted(a: MiniProjectTask, b: MiniProjectTask): number {
  const ca = a.completedAt ?? a.updatedAt ?? "";
  const cb = b.completedAt ?? b.updatedAt ?? "";
  if (ca !== cb) return ca < cb ? 1 : -1; // descending
  return (a.position ?? 0) - (b.position ?? 0);
}

/** Next-up sort: due date ascending with no-due-date last, then position.
 *  Same "soonest-deadline-first, then user-chosen ordering" rule the
 *  magazine spread used implicitly. */
function sortNextUp(a: MiniProjectTask, b: MiniProjectTask): number {
  const da = a.dueDate ?? "9999-12-31";
  const db = b.dueDate ?? "9999-12-31";
  if (da !== db) return da < db ? -1 : 1;
  return (a.position ?? 0) - (b.position ?? 0);
}

export default function TasksSection({
  miniProjectGuid,
  tasks,
  isLead,
  onMutated,
  addOpen = false,
  onCloseAdd,
  layout = "flat",
}: Props) {
  const { t } = useAppTranslation("pages");
  // Edit-tray target stored as guid (not the full task object) so that
  // when the in-tray status pill cycles and triggers a parent refetch,
  // the tray sees the *latest* task from the updated tasks[] — not a
  // stale snapshot captured at pencil-click. The derived editingTask
  // below resolves the guid against the current tasks[] each render.
  const [editingTaskGuid, setEditingTaskGuid] = useState<string | null>(null);
  const editingTask = editingTaskGuid
    ? tasks.find((task) => task.taskGuid === editingTaskGuid) ?? null
    : null;

  // DEV-1288 — read/execution detail tray. Resolved against current tasks[]
  // each render so an in-tray status change reflects without a stale snapshot.
  const [detailTaskGuid, setDetailTaskGuid] = useState<string | null>(null);
  const liveDetailTask = detailTaskGuid
    ? tasks.find((task) => task.taskGuid === detailTaskGuid) ?? null
    : null;
  // Retain the last resolved task so an in-place edit's refetch (which can
  // momentarily empty `tasks`) never blanks or closes the open tray. The
  // tray's open state is driven by detailTaskGuid (local), not by whether the
  // live tasks array currently contains it.
  const lastDetailTaskRef = useRef<MiniProjectTask | null>(null);
  if (liveDetailTask) lastDetailTaskRef.current = liveDetailTask;
  const detailTask = liveDetailTask ?? (detailTaskGuid ? lastDetailTaskRef.current : null);

  // DEV-1123. Fetch team + roles once per project; share across all card
  // rows so the assignee chip can resolve user names / role labels /
  // Lead-default fallback without N parallel network round-trips.
  const [teamMembers, setTeamMembers] = useState<MiniProjectTeamMember[]>([]);
  const [roles, setRoles] = useState<MiniProjectRole[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [tm, rs] = await Promise.all([
          fetchMiniProjectTeamMembers(miniProjectGuid),
          fetchMiniProjectRoles(miniProjectGuid),
        ]);
        if (!cancelled) {
          setTeamMembers(tm);
          setRoles(rs);
        }
      } catch {
        // Non-fatal — the assignee chip will simply not render until
        // the next successful refresh.
      }
    })();
    return () => { cancelled = true; };
  }, [miniProjectGuid]);

  // Partition once per tasks change for split layout. Cheap O(n) walk;
  // memoize so the sort cost doesn't refire on every parent re-render
  // (the parent does refetch frequently — sub.refetch / status events).
  const { completed, nextUp } = useMemo(() => {
    if (layout !== "split") return { completed: [], nextUp: tasks };
    const done: MiniProjectTask[] = [];
    const active: MiniProjectTask[] = [];
    for (const task of tasks) {
      if (task.status === "done") done.push(task);
      else active.push(task);
    }
    done.sort(sortRecentlyCompleted);
    active.sort(sortNextUp);
    return { completed: done, nextUp: active };
  }, [tasks, layout]);

  // DEV-1288 — can the current viewer act on the open detail task (add
  // note/evidence, change status)? Mirrors MiniProjectTaskCard.canActOnTask
  // and the backend MiniProjectAuth.CanUpdateOwnTaskStatus: Lead, direct
  // owner, or active holder of the task's assigned role.
  const detailCanAct = useMemo(() => {
    if (!detailTask) return false;
    if (isLead) return true;
    const me = getCurrentUserGuid().toUpperCase();
    if (!me) return false;
    if (detailTask.ownerUserGuid && detailTask.ownerUserGuid.toUpperCase() === me) return true;
    if (detailTask.assignedRoleGuid && roles) {
      const role = roles.find(
        (r) => r.roleGuid.toUpperCase() === detailTask.assignedRoleGuid!.toUpperCase(),
      );
      if (role && role.activeAssignees.some((a) => a.userGuid.toUpperCase() === me)) return true;
    }
    return false;
  }, [detailTask, isLead, roles]);

  const renderCard = (task: MiniProjectTask) => (
    <MiniProjectTaskCard
      key={task.taskGuid}
      miniProjectGuid={miniProjectGuid}
      task={task}
      isLead={isLead}
      onMutated={onMutated}
      onEdit={() => setEditingTaskGuid(task.taskGuid)}
      onOpenDetail={() => setDetailTaskGuid(task.taskGuid)}
      teamMembers={teamMembers}
      roles={roles}
    />
  );

  // Trays — only one open at a time. Keying on the active record/state
  // forces useState initializers in the tray to fire fresh on each
  // open so the form populates from the right source.
  const trays = (
    <>
      <MiniProjectTaskEditTray
        key={`task-create-${addOpen ? "open" : "closed"}`}
        mode="create"
        open={addOpen}
        miniProjectGuid={miniProjectGuid}
        onClose={() => onCloseAdd?.()}
        onMutated={() => {
          onMutated();
          onCloseAdd?.();
        }}
      />
      <MiniProjectTaskEditTray
        key={`task-edit-${editingTaskGuid ?? "none"}`}
        mode="edit"
        open={editingTask !== null}
        task={editingTask}
        miniProjectGuid={miniProjectGuid}
        // DEV-1257-followup. Non-Lead assignees can open the tray now (per
        // MiniProjectTaskCard's canActOnTask widening). The tray reads
        // isLead and gates structural fields + Save/Delete on it; status
        // pill stays available so they can mark their task done.
        isLead={isLead}
        onClose={() => setEditingTaskGuid(null)}
        onMutated={() => {
          // Do NOT clear editingTaskGuid here — the in-tray status pill
          // fires onMutated() after a cycle, and clearing the guid would
          // collapse the tray every time the user adjusts state. The
          // tray's own close paths (Cancel / Esc / scrim / Save handler)
          // close it explicitly.
          onMutated();
        }}
      />
      <MiniProjectTaskDetailTray
        key={`task-detail-${detailTaskGuid ?? "none"}`}
        open={detailTaskGuid !== null}
        task={detailTask}
        miniProjectGuid={miniProjectGuid}
        canAct={detailCanAct}
        isLead={isLead}
        onClose={() => setDetailTaskGuid(null)}
        onMutated={onMutated}
        onEdit={() => {
          // Lead hands off to the authoring tray — close detail, open edit.
          const g = detailTaskGuid;
          setDetailTaskGuid(null);
          setEditingTaskGuid(g);
        }}
      />
    </>
  );

  // ── Split layout ────────────────────────────────────────────────
  // Left: recently completed. Right: next up.
  if (layout === "split") {
    return (
      <>
        <div className={styles.taskColumns}>
          <div className={styles.taskColumn}>
            <div className={styles.taskColumnHeader}>
              {t("miniProject.sections.tasks.recentlyCompleted", {
                defaultValue: "Recently completed",
              })}
            </div>
            {completed.length === 0 ? (
              <p className={styles.rowMeta}>
                {t("miniProject.sections.tasks.noneCompleted", {
                  defaultValue: "Nothing finished yet.",
                })}
              </p>
            ) : (
              <div className={styles.taskColumnList}>
                {completed.map(renderCard)}
              </div>
            )}
          </div>
          <div className={styles.taskColumn}>
            <div className={styles.taskColumnHeader}>
              {t("miniProject.sections.tasks.nextUp", {
                defaultValue: "Next up",
              })}
            </div>
            {nextUp.length === 0 && !addOpen ? (
              <p className={styles.rowMeta}>
                {t("miniProject.sections.tasks.empty")}
              </p>
            ) : (
              <div className={styles.taskColumnList}>{nextUp.map(renderCard)}</div>
            )}
          </div>
        </div>
        {trays}
      </>
    );
  }

  // ── Flat layout (default) ───────────────────────────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {tasks.length === 0 && !addOpen ? (
        <p className={styles.rowMeta}>{t("miniProject.sections.tasks.empty")}</p>
      ) : (
        tasks.map(renderCard)
      )}
      {trays}
    </div>
  );
}
